const KEY = 'ic_generated_ideas';

// ── Persistence ───────────────────────────────────────────────────
export function loadGeneratedIdeas() {
  try { const v = localStorage.getItem(KEY); return v ? JSON.parse(v) : {}; }
  catch { return {}; }
}

export function saveGeneratedIdea(promptId, data) {
  const all = loadGeneratedIdeas();
  all[String(promptId)] = data;
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch {}
}

export function getGeneratedIdea(promptId) {
  return loadGeneratedIdeas()[String(promptId)] || null;
}

export function deleteGeneratedIdea(promptId) {
  const all = loadGeneratedIdeas();
  delete all[String(promptId)];
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch {}
}

// ── Section label heuristics by essay type ────────────────────────
function labelsForType(essayTypeLabel = '') {
  const t = essayTypeLabel.toLowerCase();
  if (t.includes('discuss'))    return ['View A — Arguments For', 'View B — Arguments Against'];
  if (t.includes('problem'))    return ['Main Causes', 'Effective Solutions'];
  if (t.includes('advantage'))  return ['Advantages', 'Disadvantages'];
  if (t.includes('opinion'))    return ['Arguments For', 'Counter-Arguments'];
  if (t.includes('two'))        return ['Question 1 — Why / Causes', 'Question 2 — Effects / Consequences'];
  return ['Main Arguments', 'Counter-Arguments'];
}

// ── Anthropic API call ────────────────────────────────────────────
export async function generateIdeaBankForPrompt(prompt, essayTypeLabel) {
  const [label1, label2] = labelsForType(essayTypeLabel);

  const res = await fetch('/api/anthropic', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-5',
      max_tokens: 1400,
      system: 'You are an expert IELTS Writing Task 2 teacher. Generate a comprehensive, specific idea bank for IELTS students. All examples must reference real countries, cities, or organisations with real data. Include at least one Middle East or Arab world example. Return ONLY valid JSON, no markdown.',
      messages: [{
        role: 'user',
        content: `Generate a full idea bank for this IELTS Writing Task 2 question.

Essay type: ${essayTypeLabel}
Question: "${prompt.text}"

Return ONLY valid JSON:
{
  "topic": "Short 3-5 word topic label",
  "task": "Write ONE body paragraph arguing [the most interesting angle of this question] — use at least ONE idea from the bank and ONE specific example.",
  "keyIdea": "One sentence capturing the central tension or debate in this question.",
  "sections": [
    {
      "label": "${label1}",
      "color": "green",
      "items": [
        "Complete sentence — specific main point with brief explanation",
        "Complete sentence — second specific point",
        "Complete sentence — third point",
        "Complete sentence — fourth point"
      ]
    },
    {
      "label": "${label2}",
      "color": "red",
      "items": [
        "Complete sentence — specific counter-point with brief explanation",
        "Complete sentence — second counter-point",
        "Complete sentence — third counter-point",
        "Complete sentence — fourth counter-point"
      ]
    },
    {
      "label": "Real Country Examples",
      "color": "teal",
      "items": [
        "UAE / Saudi Arabia / Egypt: specific fact, statistic, or policy with approximate year",
        "Europe or North America: specific example with real outcome or data",
        "Asia or Latin America: specific example",
        "Global statistic or UN/WHO/IMF data point with approximate year"
      ]
    },
    {
      "label": "Key Vocabulary",
      "color": "purple",
      "items": [
        "academic word 1", "academic phrase 2", "academic word 3",
        "academic word 4", "academic word 5", "academic word 6",
        "academic word 7", "academic word 8"
      ]
    }
  ]
}

Rules:
- Items must be complete sentences (not fragments) except vocabulary items
- Examples must be specific — name the country/city/organisation and include a real fact or approximate statistic
- Vocabulary must be appropriate for IELTS Band 7+ essays
- The task field should name the most arguable or interesting angle of the question`,
      }],
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `API error ${res.status}`);
  }

  const data = await res.json();
  const text = data.content?.[0]?.text || '{}';
  const parsed = JSON.parse(text.replace(/```json|```/g, '').trim());

  return {
    ...parsed,
    promptId:    String(prompt.id),
    promptTitle: prompt.title,
    promptText:  prompt.text,
    essayType:   essayTypeLabel,
    generatedAt: new Date().toLocaleDateString('en-GB'),
  };
}
